import { TRANCHE_REDESIGN } from 'adminConstants/routes';
import { Button } from '@mui/material';
import Page from 'components/new/Page';
import {
  PageMessagesContext,
  usePageMessagesState,
} from 'components/new/Page/usePageMessages';
import { RolePermission } from 'further-types/admin-user-role';
import { FundStatus } from 'further-types/fund';
import { Row } from 'further-ui/components/Layout';
import TabsPanel from 'further-ui/components/TabsPanel';
import { useGetRole } from 'hooks/ui/useGetRole';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import api from 'lib/trpcClient';
import { FC, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { mapTrancheToFormValues } from '../mapping';
import { TrancheData } from '../types';
import useTrancheForm, { tranchePublishableSchema } from '../useTrancheForm';
import useUsesDealDocumentation from '../useUsesDealDocumentation';
import DataRoom from './DataRoom';
import { TrancheDotColor } from './dotColors';
import StatusSelect from './StatusSelect';
import { getHiddenFundStatuses } from './statusOptions';
import TrancheEdit from './Tranche';
import DraftTrancheBanner from './Tranche/DraftTrancheBanner';

enum TrancheFormTab {
  Tranche = 'tranche',
  DataRoom = 'data-room',
}

type Props = {
  tranche?: TrancheData;
};

const TrancheFormV2: FC<Props> = ({ tranche }) => {
  const heading = tranche?.fundName ? tranche.fundName : 'Untitled tranche';
  const { messages, contextValue } = usePageMessagesState();
  const { addMessage, removeMessage } = contextValue;

  const updateStatus = api.fund.updateStatus.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Tranche status updated successfully.',
      invalidations: [['tranches']],
    }),
  );

  const { firmId: usersFirmId } = useGetRole();
  const form = useTrancheForm({
    firmId: usersFirmId,
    values: tranche ? mapTrancheToFormValues(tranche) : undefined,
  });
  const [serverStatus, setServerStatus] = useState(tranche?.fundStatus);

  useEffect(() => {
    setServerStatus(tranche?.fundStatus);
  }, [tranche?.fundStatus]);

  const usesDealDocumentation = useUsesDealDocumentation(tranche?.tag);

  const isCreating = !tranche?.id;
  const hasInvestments = (tranche?.investments ?? []).length > 0;

  const isDraftTranche = serverStatus === FundStatus.Draft;
  const isPublishable =
    tranche && isDraftTranche
      ? tranchePublishableSchema.safeParse({
          ...mapTrancheToFormValues(tranche),
          usesDealDocumentation,
        }).success
      : null;

  const [canEdit, canCreateDataRoom] = useHasPermissions([
    RolePermission.EditTranche,
    RolePermission.CreateDataRoom,
  ]);
  const history = useHistory();

  const hiddenStatuses: Array<FundStatus> = [
    ...getHiddenFundStatuses(isCreating, hasInvestments),
    ...(isPublishable === false ? [FundStatus.Open] : []),
  ];

  const persistStatus = useCallback(
    (newStatus: FundStatus) => {
      if (!tranche?.id) return;
      updateStatus.mutate(
        { id: tranche.id, firmId: tranche.firmId, fundStatus: newStatus },
        {
          onSuccess: () => {
            setServerStatus(newStatus);
            form.resetField('fundStatus', { defaultValue: newStatus });
          },
        },
      );
    },
    [tranche?.id, tranche?.firmId, updateStatus.mutate, form],
  );

  const handleStatusChange = (newStatus: FundStatus) =>
    persistStatus(newStatus);

  const handleOpenTranche = useCallback(
    () => persistStatus(FundStatus.Open),
    [persistStatus],
  );

  useEffect(() => {
    if (!isDraftTranche) {
      removeMessage('draft-status');
      return;
    }
    addMessage({
      key: 'draft-status',
      variant: 'neutral',
      centered: true,
      content: (
        <DraftTrancheBanner
          isPublishable={isPublishable ?? false}
          onOpenTranche={handleOpenTranche}
        />
      ),
    });
  }, [
    isDraftTranche,
    isPublishable,
    addMessage,
    removeMessage,
    handleOpenTranche,
  ]);

  return (
    <PageMessagesContext.Provider value={contextValue}>
      <Page
        customActions={
          <Row centered spacing="sm" fullWidth>
            <StatusSelect
              value={serverStatus ?? FundStatus.Draft}
              onChange={handleStatusChange}
              isSaving={updateStatus.isPending}
              disabled={!canEdit}
              hiddenStatuses={hiddenStatuses}
            />
            {canCreateDataRoom && !tranche?.dataRoomId && (
              <Button
                size="large"
                variant="contained"
                onClick={() => history.push(`#${TrancheFormTab.DataRoom}`)}
              >
                Add data room
              </Button>
            )}
          </Row>
        }
        messages={messages}
        heading={heading}
        goBackAction={TRANCHE_REDESIGN}
        spacing="lg"
      >
        <TabsPanel
          fullHeight
          useTabsUrls
          tabs={[
            {
              key: TrancheFormTab.Tranche,
              label: 'Tranche',
              dotColor:
                isDraftTranche && isPublishable === false
                  ? TrancheDotColor.Draft
                  : undefined,
              content: (
                <TrancheEdit
                  form={form}
                  tranche={tranche}
                  isPublishable={isPublishable}
                  isCreating={isCreating}
                  hasInvestments={hasInvestments}
                  isStatusUpdating={updateStatus.isPending}
                />
              ),
              urlParam: TrancheFormTab.Tranche,
            },
            ...(tranche && serverStatus !== FundStatus.Draft
              ? [
                  {
                    key: TrancheFormTab.DataRoom,
                    label: 'Data room',
                    content: tranche ? (
                      <DataRoom
                        setLiveDisabled={
                          serverStatus !== FundStatus.Open &&
                          serverStatus !== FundStatus.Closed
                        }
                        linkedToId={tranche.id}
                        dataRoomId={tranche.dataRoomId}
                      />
                    ) : null,
                    urlParam: TrancheFormTab.DataRoom,
                  },
                ]
              : []),
          ]}
        />
      </Page>
    </PageMessagesContext.Provider>
  );
};

export default TrancheFormV2;
